import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { NavGroup } from '../../../config/navigation';
import { isActiveGroup, isActivePath } from '../../../utils/navigation';

interface MobileNavSectionProps {
  group: NavGroup;
  onNavigate: () => void;
}

const MobileNavSection: React.FC<MobileNavSectionProps> = ({ group, onNavigate }) => {
  const location = useLocation();
  const groupActive = isActiveGroup(group, location.pathname);
  const [isExpanded, setIsExpanded] = useState(groupActive);

  if (group.href) {
    const active = isActivePath(group.href, location.pathname);
    return (
      <Link
        to={group.href}
        onClick={onNavigate}
        className={`flex items-center gap-3 py-3 px-4 rounded-lg text-base font-medium transition-colors ${
          active
            ? 'text-brand bg-brand-light'
            : 'text-text-primary hover:text-brand hover:bg-surface-1'
        }`}
      >
        {group.icon && <group.icon className="w-5 h-5" />}
        {group.label}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className={`w-full flex items-center justify-between py-3 px-4 rounded-lg text-base font-medium transition-colors ${
          groupActive
            ? 'text-brand'
            : 'text-text-primary hover:text-brand hover:bg-surface-1'
        }`}
      >
        <span className="flex items-center gap-3">
          {group.icon && <group.icon className="w-5 h-5" />}
          {group.label}
        </span>
        <ChevronDown
          className={`w-5 h-5 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded && group.items && (
        <div className="ml-8 mt-1 space-y-0.5 border-l-2 border-border pl-4">
          {group.items.map((item) => {
            const active = isActivePath(item.href, location.pathname);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onNavigate}
                className={`block py-2 px-3 rounded-md text-sm transition-colors ${
                  active
                    ? 'text-brand bg-brand-light font-medium'
                    : 'text-text-secondary hover:text-brand hover:bg-surface-1'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export { MobileNavSection };
